<template>
    <div class="min-h-screen bg-slate-50 p-8 font-sans">
        <div class="max-w-6xl mx-auto">
            
            <div class="flex items-center justify-between mb-8">
                <div>
                    <h2 class="text-3xl font-black text-slate-800 tracking-tight">
                        {{ prettyDate }}
                    </h2>
                    <div class="flex items-center gap-2 mt-1">
                        <span class="flex h-2 w-2 rounded-full bg-emerald-500"></span>
                        <p class="text-sm font-medium text-slate-500">
                            {{ interventions.length }} mission{{ interventions.length > 1 ? 's' : '' }} programmée{{ interventions.length > 1 ? 's' : '' }}
                        </p>
                    </div>
                </div>
                
                <button class="px-5 py-2.5 bg-slate-900 text-white rounded-xl font-semibold shadow-lg shadow-slate-200 hover:bg-slate-800 transition-all active:scale-95">
                    + Nouvelle intervention
                </button>
            </div>

            <div class="bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.05)] border border-slate-100 overflow-hidden p-2">
                <div ref="dp" class="daypilot-modern-wrap"></div>
            </div>
            
        </div>
    </div>
</template>

<script>
import { DayPilot } from "daypilot-pro-javascript";

export default {
    name: "PlanningDay",

    props: {
        date: {
            type: String,
            required: true
        },
        interventions: {
            type: Array,
            required: true
        },
        preview: {
            type: Object,
            default: null
        }
    },

    watch: {
        date() {
            this.refresh();
        },
        interventions() {
            this.refresh();
        },
        preview: {
            handler() {
                this.refresh();
            },
            deep: true   // 🔥 OBLIGATOIRE
        }
    },

    data() {
        return {
            calendar: null
        };
    },

    computed: {
        prettyDate() {
            return new DayPilot.Date(this.date).toString("dddd dd MMMM yyyy", "fr-fr");
        },

        openingHour() {
            return this.timeToDecimal(
                this.$account.account.societe.heure_ouverture
            );
        },

        closingHour() {
            return this.timeToDecimal(
                this.$account.account.societe.heure_fermeture
            );
        }
    },

    methods: {
        refresh() {
            if (!this.calendar) return;

            this.calendar.update({
                startDate: this.date,
                events: this.buildEvents()
            });
        },

        normalizeTime(time) {
            if (!time) return "00:00:00";
            return time.length === 5 ? time + ":00" : time;
        },

        /** Convertit tes événements en events DayPilot */
        buildEvents() {
            let interventions = Array.isArray(this.interventions)
                ? this.interventions
                : this.interventions.items || [];

            const events = interventions.map(e => {
                const date = this.normalizeDate(e.date_intervention);

                const start = `${date}T${this.normalizeTime(e.heure_debut)}`;
                const end   = `${date}T${this.normalizeTime(e.heure_fin)}`;
                const adresse = e.adresse.code_postal + ' ' + e.adresse.ville;

                return {
                    id: String(e.id),
                    text: `${e.statut ? '[' + e.statut.libelle + '] ' : ''} [${adresse}] ${e.reference}\n${e.heure_debut} → ${e.heure_fin}`,
                    start: new DayPilot.Date(start),
                    end: new DayPilot.Date(end),
                    backColor: e.isNew ? "#ff9800" : (e.statut ? e.statut.couleur :"#4781de"),
                    borderColor: e.isNew ? "#e68a00" : "#3563aa",
                    fontColor: "white"
                };
            });

            // Trajet
            interventions.forEach(intervention => {
                // Trajet before
                if (intervention.trajet_temps) {
                    const date = this.normalizeDate(intervention.date_intervention);
                    const finTrajet = new DayPilot.Date(
                        `${date}T${this.normalizeTime(intervention.heure_debut)}`
                    );
                    const debutTrajet = finTrajet.addMinutes(-intervention.trajet_temps - 5);

                    events.push({
                        id: 'trajet-' + intervention.id,
                        text: 'Trajet départ | ' + (intervention.trajet_temps ? `${intervention.trajet_temps} min - ${intervention.trajet_distance_km} km - Début: ${debutTrajet} - Fin ${finTrajet}` : ''),
                        start: debutTrajet,
                        end: finTrajet,
                        backColor: "#6b7280",
                        borderColor: "#4b5563",
                        fontColor: "white",
                    });
                }

                // Trajet after
                if (intervention.trajet_temps_retour) {
                    const date = this.normalizeDate(intervention.date_intervention);
                    const debutTrajet = new DayPilot.Date(
                        `${date}T${this.normalizeTime(intervention.heure_fin)}`
                    );
                    const finTrajet = debutTrajet.addMinutes(intervention.trajet_temps_retour);

                    events.push({
                        id: 'trajet-retour-' + intervention.id,
                        text: 'Trajet retour | ' + (intervention.trajet_temps_retour ? `${intervention.trajet_temps_retour} min - ${intervention.trajet_distance_km_retour} km - Début: ${debutTrajet} - Fin ${finTrajet}` : ''),
                        start: debutTrajet,
                        end: finTrajet,
                        backColor: "#6b7280",
                        borderColor: "#4b5563",
                        fontColor: "white",
                    });
                }
            });

            // 👀 PREVIEW (intervention en cours de planification)
            if (this.preview &&
                this.preview.date_intervention === this.date &&
                this.preview.heure_debut &&
                this.preview.heure_fin
            ) {
                events.push({
                    id: "preview",
                    text: `Prévisualisation\n${this.preview.heure_debut} → ${this.preview.heure_fin}\n${this.preview.adresse}`,
                    start: new DayPilot.Date(
                        `${this.preview.date_intervention}T${this.normalizeTime(this.preview.heure_debut)}`
                    ),
                    end: new DayPilot.Date(
                        `${this.preview.date_intervention}T${this.normalizeTime(this.preview.heure_fin)}`
                    ),
                    backColor: "#22c55e",
                    borderColor: "#16a34a",
                    fontColor: "white",
                    moveDisabled: true,
                    resizeDisabled: true,
                    clickDisabled: true,
                    opacity: 70
                });

                // Trajet preview
                // Avant l'intervention
                if (this.preview.trajet_temps) {
                    const date = this.normalizeDate(this.preview.date_intervention);
                    const finTrajet = new DayPilot.Date(
                        `${date}T${this.normalizeTime(this.preview.heure_debut)}`
                    );
                    const debutTrajet = finTrajet.addMinutes(-this.preview.trajet_temps - 5);

                    events.push({
                        id: 'trajet-preview',
                        text: 'Trajet départ | ' + (this.preview.trajet_temps ? `${this.preview.trajet_temps} min - ${this.preview.trajet_distance_km} km - Début: ${debutTrajet} - Fin ${finTrajet}` : ''),
                        start: debutTrajet,
                        end: finTrajet,
                        backColor: "#6b7280",
                        borderColor: "#4b5563",
                        fontColor: "white",
                    });
                }

                // Trajet retour preview
                if (this.preview.trajet_temps_retour) {
                    const date = this.normalizeDate(this.preview.date_intervention);
                    const debutTrajet = new DayPilot.Date(
                        `${date}T${this.normalizeTime(this.preview.heure_fin)}`
                    );
                    const finTrajet = debutTrajet.addMinutes(this.preview.trajet_temps_retour);

                    events.push({
                        id: 'trajet-retour-preview',
                        text: 'Trajet retour preview | ' + (this.preview.trajet_temps_retour ? `${this.preview.trajet_temps_retour} min - ${this.preview.trajet_distance_km_retour} km - Début: ${debutTrajet} - Fin ${finTrajet}` : ''),
                        start: debutTrajet,
                        end: finTrajet,
                        backColor: "#6b7280",
                        borderColor: "#4b5563",
                        fontColor: "white",
                    });
                }
            }

            return events;
        },

        /** Initialise le calendrier Day view */
        timeToDecimal(time) {
            if (!time) return 0;

            const [hours, minutes = 0] = time.split(":").map(Number);
            return hours + minutes / 60;
        },

        initCalendar() {
            if (!this.calendar) {
                this.calendar = new DayPilot.Calendar(this.$refs.dp, {
                    viewType: "Day",
                    timeFormat: "Clock24Hours",
                    locale: "fr-fr",
                    cellDuration: 15,
                    snapToGrid: true,
                    startDate: this.date,
                    durationBarVisible: true,
                    dayBeginsHour: this.openingHour,
                    dayEndsHour: this.closingHour,
                    showNonBusiness: false,
                    timeRangeSelectedHandling: "Disabled",
                    eventMoveHandling: "Disabled",
                    eventResizeHandling: "Disabled",
                    eventClickHandling: "Bubble",
                    onEventClicked: args => {
                        // On ne remonte pas la preview
                        if (args.e.id() === "preview") return;
                        // On ne remonte pas les trajets
                        if (args.e.id().startsWith("trajet")) return;
                        this.$emit("click-event", args.e.data);
                    }
                });
                this.calendar.init();
            }

            this.refresh();
        },

        normalizeDate(date) {
            if (!date) return null;
            return date.split("T")[0]; // garde YYYY-MM-DD
        },
    },

    mounted() {
        this.initCalendar();

        const el = document.getElementsByClassName('calendar_default_corner')

        // if (el.length > 0 && el[0]) {
        //     el[0].innerHTML = ''
        //     el[0].style.backgroundColor = '#F3F3F3'
        // }
    },

    watch: {
        interventions() {
            this.refresh();
        },

        date() {
            this.refresh();
        },

        preview: {
            handler() {
                this.refresh();
            },
            deep: true   // 🔥 OBLIGATOIRE
        }
    },
};
</script>

<style scoped>
/* Conteneur Global */
.daypilot-modern-wrap {
    height: 600px;
    border: none !important;
}

/* Supprimer toutes les bordures par défaut moches */
:deep(.calendar_default_main), 
:deep(.calendar_default_scroll),
:deep(.calendar_default_corner),
:deep(.calendar_default_colheader_back) {
    border: none !important;
    background-color: transparent !important;
}

/* Style des heures (Colonne de gauche) */
:deep(.calendar_default_rowheader) {
    background: white !important;
    border: none !important;
    color: #cbd5e1 !important; /* Slate 300 */
    font-weight: 500 !important;
    font-size: 13px !important;
}

:deep(.calendar_default_rowheader_inner) {
    border-bottom: none !important;
    padding-right: 15px !important;
}

/* Style des cellules de la grille */
:deep(.calendar_default_cell) {
    background-color: white !important;
    border-bottom: 1px solid #f1f5f9 !important; /* Ligne très fine */
    border-right: 1px solid #f1f5f9 !important;
}

/* Suppression de la bordure droite sur la dernière colonne */
:deep(.calendar_default_cell:last-child) {
    border-right: none !important;
}

/* Header du jour (Haut) */
:deep(.calendar_default_colheader) {
    background: white !important;
    color: #1e293b !important;
    font-weight: 700 !important;
    font-size: 14px !important;
    height: 50px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border-bottom: 2px solid #f8fafc !important;
}

/* Les Événements (Si tu en as) */
:deep(.calendar_default_event) {
    border-radius: 12px !important;
    border: none !important;
    background: #4f46e5 !important; /* Indigo 600 */
    box-shadow: 0 4px 12px rgba(79, 70, 229, 0.2) !important;
    color: white !important;
    font-size: 12px !important;
    font-weight: 600 !important;
}

:deep(.calendar_default_event_inner) {
    padding: 8px !important;
    border-radius: 12px !important;
}

/* Barre de chargement */
:deep(.calendar_default_loading) {
    background: rgba(255, 255, 255, 0.8) !important;
    color: #4f46e5 !important;
    font-weight: bold !important;
    border-radius: 20px !important;
}
</style>